import { api } from './api';
import type { MagicLinkRequestResult } from '@/features/auth/magicLink';

// ─── Matters ───────────────────────────────────────────────────

export interface PortalBarristerSummary {
  id: string;
  publicSlug: string | null;
  callYear: number | null;
  silkYear: number | null;
  user: {
    firstName: string;
    lastName: string;
    avatarUrl: string | null;
  };
}

export interface PortalMatter {
  id: string;
  caseNumber: string;
  caseName: string;
  caseType: string;
  status: string;
  priority: string;
  description: string | null;
  openedDate: string | null;
  limitationDate: string | null;
  closedDate: string | null;
  feeEstimate: string | null;
  currency: string;
  practiceArea: { name: string; slug: string } | null;
  court: { name: string; shortName: string } | null;
  leadBarrister: PortalBarristerSummary | null;
  juniorBarrister: Pick<PortalBarristerSummary, 'id' | 'user'> | null;
  layClient: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    company: string | null;
  } | null;
  updatedAt: string;
  createdAt: string;
}

export async function fetchMyMatters(): Promise<PortalMatter[]> {
  const res = await api.get('/solicitor-portal/matters');
  return res.data?.data ?? res.data;
}

export async function fetchMyMatter(id: string): Promise<PortalMatter & { hearings: PortalHearing[] }> {
  const res = await api.get(`/solicitor-portal/matters/${id}`);
  return res.data?.data ?? res.data;
}

// ─── Calendar (role-filtered) ─────────────────────────────────

export interface PortalHearing {
  id: string;
  caseId: string;
  hearingDate: string;
  durationMinutes: number;
  hearingType: string;
  status: string;
  courtroom: string | null;
  court: { name: string; shortName: string } | null;
  case?: { caseNumber: string; caseName: string };
  viewMode?: 'readonly';
}

export interface PortalConference {
  id: string;
  caseId: string | null;
  title: string;
  description: string | null;
  startAt: string;
  endAt: string;
  location: string | null;
  videoUrl: string | null;
  case?: { caseNumber: string; caseName: string } | null;
  viewMode?: 'attendee';
}

export interface PortalCalendar {
  hearings: PortalHearing[];
  conferences: PortalConference[];
}

export async function fetchMyCalendar(range?: { from?: string; to?: string }): Promise<PortalCalendar> {
  const res = await api.get('/solicitor-portal/calendar', { params: range ?? {} });
  return res.data?.data ?? res.data;
}

// ─── Fee notes ────────────────────────────────────────────────

export interface PortalFeeNote {
  id: string;
  feeNoteNumber: string;
  status: string;
  issueDate: string;
  dueDate: string;
  subtotal: string;
  vatTotal: string;
  total: string;
  amountPaid: string;
  currency: string;
  pdfUrl: string | null;
  sentAt: string | null;
  case: { id: string; caseNumber: string; caseName: string };
}

export async function fetchMyFeeNotes(): Promise<PortalFeeNote[]> {
  const res = await api.get('/solicitor-portal/fee-notes');
  return res.data?.data ?? res.data;
}

// ─── Documents ────────────────────────────────────────────────

export interface PortalDocument {
  id: string;
  name: string;
  description: string | null;
  mimeType: string;
  fileSizeBytes: string;
  version: number;
  category: string;
  tags: string[];
  createdAt: string;
  case: { id: string; caseNumber: string; caseName: string };
  folder: { id: string; name: string } | null;
  uploadedBy: {
    id: string;
    firstName: string;
    lastName: string;
    memberType: string;
  };
}

export async function fetchMyDocuments(matterId?: string): Promise<PortalDocument[]> {
  const res = await api.get('/solicitor-portal/documents', {
    params: matterId ? { matterId } : {},
  });
  return res.data?.data ?? res.data;
}

// ─── Documents upload (presigned PUT) ─────────────────────────

export interface RequestUploadPayload {
  matterId: string;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
}

export interface RequestUploadResult {
  uploadUrl: string;
  fileKey: string;
  expiresInSeconds: number;
}

export async function requestDocumentUploadUrl(
  payload: RequestUploadPayload,
): Promise<RequestUploadResult> {
  const res = await api.post('/solicitor-portal/documents/upload-url', payload);
  return res.data?.data ?? res.data;
}

export interface ConfirmUploadPayload {
  matterId: string;
  fileKey: string;
  name: string;
  description?: string;
}

export async function confirmDocumentUpload(
  payload: ConfirmUploadPayload,
): Promise<PortalDocument> {
  const res = await api.post('/solicitor-portal/documents', payload);
  return res.data?.data ?? res.data;
}

/**
 * Download a client-visible document through the AUTHENTICATED, integrity-verified
 * endpoint — the server re-hashes the bytes against their upload baseline before
 * sending, so no presigned URL is exposed to the browser and no corrupted file is
 * served. Triggers a browser download using the server-supplied filename.
 */
export async function downloadSolicitorDocument(documentId: string, fallbackName?: string): Promise<void> {
  const res = await api.get<ArrayBuffer>(`/solicitor-portal/documents/${documentId}/download`, {
    responseType: 'arraybuffer',
  });
  const mime = (res.headers['content-type'] as string | undefined) ?? 'application/octet-stream';
  const disposition = res.headers['content-disposition'] as string | undefined;
  const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const name = (match?.[1] ? decodeURIComponent(match[1]) : undefined) ?? fallbackName ?? 'document';

  const blobUrl = URL.createObjectURL(new Blob([res.data], { type: mime }));
  try {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

// ─── Clerk-initiated invite ───────────────────────────────────

export interface InviteSolicitorPayload {
  email: string;
  firstName: string;
  lastName: string;
  firmContactId: string;
  jobTitle?: string;
  sraNumber?: string;
}

export interface InviteSolicitorResult {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    status: string;
    firmContactId: string;
  };
  invite: MagicLinkRequestResult;
}

export async function inviteSolicitor(
  payload: InviteSolicitorPayload,
): Promise<InviteSolicitorResult> {
  const res = await api.post('/solicitor-portal/invite', payload);
  return res.data?.data ?? res.data;
}

// ─── Messaging (Phase F) ──────────────────────────────────────

export interface PortalThreadParticipant {
  id: string;
  userId: string;
  role: string;
  lastReadSeq: number;
  joinedAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    memberType: string;
  };
}

export interface PortalThread {
  id: string;
  chambersId: string;
  matterId: string | null;
  kind: string;
  subject: string | null;
  privilege: string;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string | null;
  case: { id: string; caseNumber: string; caseName: string } | null;
  participants: PortalThreadParticipant[];
}

export interface PortalMessageAttachment {
  id: string;
  documentId: string;
  document: {
    id: string;
    name: string;
    mimeType: string;
    fileSizeBytes: number | string;
  };
}

export interface PortalMessage {
  id: string;
  threadId: string;
  senderUserId: string | null;
  body: string;
  sequence: number;
  createdAt: string;
  senderUser: {
    id: string;
    firstName: string;
    lastName: string;
    memberType: string;
  } | null;
  attachments?: PortalMessageAttachment[];
}

export async function fetchMyThreads(matterId?: string): Promise<PortalThread[]> {
  const res = await api.get('/solicitor-portal/threads', {
    params: matterId ? { matterId } : {},
  });
  return res.data?.data ?? res.data;
}

export async function fetchMyThread(threadId: string): Promise<PortalThread> {
  const res = await api.get(`/solicitor-portal/threads/${threadId}`);
  return res.data?.data ?? res.data;
}

export async function fetchMyThreadMessages(
  threadId: string,
  opts?: { beforeSequence?: number; limit?: number },
): Promise<PortalMessage[]> {
  const params: Record<string, string> = {};
  if (opts?.beforeSequence) params.beforeSequence = String(opts.beforeSequence);
  if (opts?.limit) params.limit = String(opts.limit);
  const res = await api.get(`/solicitor-portal/threads/${threadId}/messages`, { params });
  return res.data?.data ?? res.data;
}

export async function sendMyThreadMessage(
  threadId: string,
  text: string,
  documentIds?: string[],
): Promise<PortalMessage> {
  const res = await api.post(`/solicitor-portal/threads/${threadId}/messages`, {
    text,
    documentIds,
  });
  return res.data?.data ?? res.data;
}

export async function markMyThreadRead(
  threadId: string,
  upToSequence: number,
): Promise<void> {
  await api.post(`/solicitor-portal/threads/${threadId}/read`, { upToSequence });
}

export interface PortalTimelineEvent {
  id: string;
  eventType: string;
  occurredAt: string;
  payload: Record<string, unknown>;
}

export async function fetchMyMatterTimeline(matterId: string): Promise<PortalTimelineEvent[]> {
  const res = await api.get<{ data: PortalTimelineEvent[] }>(
    `/solicitor-portal/matters/${matterId}/timeline`,
  );
  return res.data.data ?? [];
}

/**
 * Start a conversation from the portal.
 *
 * Deliberately takes a MATTER rather than a recipient: the server derives who in chambers
 * receives it from that matter's own team, so this screen never becomes a way to look up
 * Jurivoxa users.
 */
export async function startMyThread(input: {
  matterId: string;
  subject: string;
  text: string;
}): Promise<PortalThread> {
  const res = await api.post('/solicitor-portal/threads', input);
  return (res.data?.data ?? res.data) as PortalThread;
}
